(() => {
  const behavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const animations = new WeakMap();
  function stopMotion(track) {
    const animation = animations.get(track);
    if (animation) cancelAnimationFrame(animation.frame);
    animations.delete(track);
    track.classList.remove('cv-is-settling');
  }
  function slideTo(track, left) {
    stopMotion(track);
    const start = track.scrollLeft;
    const target = Math.max(0, Math.min(left, track.scrollWidth - track.clientWidth));
    if (behavior() === 'auto' || Math.abs(target - start) < 1) {
      track.scrollLeft = target;
      return;
    }
    track.classList.add('cv-is-settling');
    const duration = Math.min(360, Math.max(220, Math.abs(target - start) * .3));
    const animation = {frame:0, time:performance.now()};
    animations.set(track, animation);
    const tick = now => {
      if (animations.get(track) !== animation) return;
      const progress = Math.min(1, (now - animation.time) / duration);
      track.scrollLeft = start + (target - start) * (1 - Math.pow(1 - progress, 3));
      if (progress < 1) animation.frame = requestAnimationFrame(tick);
      else {
        animations.delete(track);
        track.classList.remove('cv-is-settling');
      }
    };
    animation.frame = requestAnimationFrame(tick);
  }
  function installDrag(track, settle) {
    let gesture = null, suppressClick = false;
    track.dataset.cvDraggable = 'true';
    track.addEventListener('dragstart', event => event.preventDefault());
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse') { stopMotion(track); suppressClick = false; return; }
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const interactive = event.target.closest('button,input,select,textarea,summary,video,iframe,model-viewer');
      if (interactive && !interactive.matches('.product__media-toggle')) return;
      stopMotion(track);
      suppressClick = false;
      gesture = {id:event.pointerId, x:event.clientX, y:event.clientY, left:track.scrollLeft, dx:0, velocity:0, lastX:event.clientX, lastTime:event.timeStamp, dragging:false};
    });
    track.addEventListener('pointermove', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (!gesture.dragging) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx) / 1.35) { gesture = null; return; }
        gesture.dragging = true;
        track.classList.add('cv-is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      gesture.dx = dx;
      gesture.velocity = (event.clientX - gesture.lastX) / Math.max(1, event.timeStamp - gesture.lastTime);
      gesture.lastX = event.clientX;
      gesture.lastTime = event.timeStamp;
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = gesture.left - dx;
    });
    const finish = event => {
      if (!gesture || (event.pointerId !== undefined && event.pointerId !== gesture.id)) return;
      const saved = gesture;
      gesture = null;
      if (track.hasPointerCapture(saved.id)) track.releasePointerCapture(saved.id);
      if (!saved.dragging) return;
      if (event.timeStamp - saved.lastTime > 100) saved.velocity = 0;
      suppressClick = true;
      settle(saved, event.type !== 'pointerup');
      track.classList.remove('cv-is-dragging');
    };
    track.addEventListener('pointerup', finish);
    track.addEventListener('pointercancel', finish);
    track.addEventListener('lostpointercapture', finish);
    track.addEventListener('pointerleave', event => { if (gesture && !gesture.dragging) finish(event); });
    track.addEventListener('click', event => {
      if (!suppressClick || event.detail === 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClick = false;
    }, true);
  }
  function installGallery(gallery) {
    if (gallery.dataset.cvInstalled) return;
    const track = gallery.querySelector('[id^="Slider-Gallery-"]');
    const controls = gallery.querySelector('.cv-gallery-controls');
    if (!track || !controls) return;
    gallery.dataset.cvInstalled = 'true';
    const dots = controls.querySelector('.cv-gallery-dots');
    const prev = controls.querySelector('[data-cv-prev]');
    const next = controls.querySelector('[data-cv-next]');
    let items = [], dotTargets = [], current = 0, previousItem, frame;
    const visibleItems = () => [...track.querySelectorAll('[data-media-id]')].filter(item => item.getBoundingClientRect().width > 0 && !item.hidden);
    const targetLeft = item => item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    const move = index => {
      const target = items[Math.max(0, Math.min(index, items.length - 1))];
      if (target) slideTo(track, targetLeft(target));
    };
    function sync() {
      if (!items.length) return;
      current = items.reduce((best, item, index) => Math.abs(targetLeft(item) - track.scrollLeft) < Math.abs(targetLeft(items[best]) - track.scrollLeft) ? index : best, 0);
      const activeDot = Math.round(current / Math.max(1, items.length - 1) * Math.max(0, dotTargets.length - 1));
      [...dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === activeDot)));
      prev.disabled = current === 0;
      next.disabled = current === items.length - 1;
      if (previousItem !== items[current]) {
        previousItem = items[current];
        items.forEach(item => item.classList.toggle('is-active', item === previousItem));
        gallery.playActiveMedia?.(previousItem);
      }
    }
    function rebuild() {
      const focusedId = dots.contains(document.activeElement) ? document.activeElement.dataset.mediaId : null;
      items = visibleItems();
      const count = Math.min(5, items.length);
      dotTargets = Array.from({length:count}, (_, index) => Math.round(index * (items.length - 1) / Math.max(1, count - 1)));
      dots.replaceChildren(...dotTargets.map(target => {
        const item = items[target];
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cv-gallery-dot';
        dot.dataset.mediaId = item.dataset.mediaId;
        dot.setAttribute('aria-label', (gallery.dataset.cvDotLabel || '').replace('[index]', target + 1));
        dot.setAttribute('aria-controls', track.id);
        dot.addEventListener('click', () => move(target));
        return dot;
      }));
      controls.hidden = items.length < 2;
      gallery.classList.add('cv-carousel-ready');
      gallery.querySelector('[id^="GalleryViewer-"]')?.resetPages?.();
      sync();
      if (focusedId) [...dots.children].find(dot => dot.dataset.mediaId === focusedId)?.focus({preventScroll: true});
    }
    prev.addEventListener('click', () => move(current - 1));
    next.addEventListener('click', () => move(current + 1));
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(current + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    track.tabIndex = 0;
    installDrag(track, (gesture, cancelled) => {
      const startIndex = items.reduce((best,item,index) => Math.abs(targetLeft(item) - gesture.left) < Math.abs(targetLeft(items[best]) - gesture.left) ? index : best,0);
      const threshold = Math.min(72, Math.max(32, track.clientWidth * .1));
      const advance = !cancelled && (Math.abs(gesture.dx) >= threshold || (Math.abs(gesture.dx) >= 12 && Math.abs(gesture.velocity) > .35));
      move(startIndex + (advance ? (gesture.dx < 0 ? 1 : -1) : 0));
    });
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    }, {passive: true});
    new MutationObserver(rebuild).observe(track, {childList: true});
    new ResizeObserver(rebuild).observe(track);
    rebuild();
  }
  function installRelated(carousel) {
    if (carousel.dataset.cvInstalled) return;
    const track = carousel.querySelector('.cv-related-track');
    const controls = carousel.querySelector('.cv-related-controls');
    if (!track || !controls) return;
    carousel.dataset.cvInstalled = 'true';
    const prev = controls.querySelector('[data-cv-prev]');
    const next = controls.querySelector('[data-cv-next]');
    const counter = controls.querySelector('.cv-related-counter');
    const dots = controls.querySelector('.cv-related-dots');
    let items = [], offsets = [0], current = 0, frame;
    const left = item => item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    const moveTo = index => slideTo(track, offsets[Math.max(0,Math.min(index,offsets.length - 1))]);
    const sync = () => {
      const max = Math.max(0,track.scrollWidth - track.clientWidth);
      controls.hidden = max < 3;
      prev.disabled = track.scrollLeft < 3;
      next.disabled = track.scrollLeft > max - 3;
      const bounds = track.getBoundingClientRect();
      const first = items.findIndex(item => item.getBoundingClientRect().right > bounds.left + 3);
      const last = items.reduce((value,item,index) => item.getBoundingClientRect().left < bounds.right - 3 ? index : value,first);
      counter.textContent = first < 0 ? '' : (first + 1) + '–' + (last + 1) + ' / ' + items.length;
      current = offsets.reduce((best,offset,index) => Math.abs(offset - track.scrollLeft) < Math.abs(offsets[best] - track.scrollLeft) ? index : best,0);
      const activeDot = Math.round(Math.max(0, Math.min(1, track.scrollLeft / Math.max(1, max))) * Math.max(0, dots.children.length - 1));
      [...dots.children].forEach((dot,index) => dot.setAttribute('aria-current',String(index === activeDot)));
    };
    const rebuild = () => {
      items = [...track.children].filter(item => item.getBoundingClientRect().width > 0);
      const max = Math.max(0,track.scrollWidth - track.clientWidth);
      offsets = items.map(item => Math.min(max,Math.max(0,left(item))));
      offsets = [...new Set([0,...offsets,max].map(offset => Math.round(offset)))];
      const image = items[0]?.querySelector('.card__inner');
      if (image) carousel.style.setProperty('--cv-related-arrow-top', (image.getBoundingClientRect().top - carousel.getBoundingClientRect().top + image.getBoundingClientRect().height / 2) + 'px');
      dots.replaceChildren(...Array.from({length:max > 3 ? 5 : 0}, (_,index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cv-related-dot';
        dot.setAttribute('aria-label',(carousel.dataset.cvLabel || '') + ' ' + (index + 1));
        dot.setAttribute('aria-controls',track.id);
        dot.addEventListener('click',() => {
          const target = max * index / 4;
          const nearest = offsets.reduce((best,offset,i) => Math.abs(offset - target) < Math.abs(offsets[best] - target) ? i : best,0);
          moveTo(nearest);
        });
        return dot;
      }));
      sync();
    };
    prev.addEventListener('click',() => { sync(); moveTo(current - 1); });
    next.addEventListener('click',() => { sync(); moveTo(current + 1); });
    track.tabIndex = 0;
    track.addEventListener('keydown',event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        moveTo(current + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    track.addEventListener('scroll',() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    },{passive:true});
    installDrag(track,() => { sync(); moveTo(current); });
    new ResizeObserver(rebuild).observe(track);
    new MutationObserver(rebuild).observe(track,{childList:true});
    rebuild();
  }
  function scan(root = document) {
    if (root.matches?.('[data-cv-gallery]')) installGallery(root);
    if (root.matches?.('[data-cv-related]')) installRelated(root);
    root.querySelectorAll?.('[data-cv-gallery]').forEach(installGallery);
    root.querySelectorAll?.('[data-cv-related]').forEach(installRelated);
  }
  scan();
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
    if (node.nodeType === 1) scan(node);
  }))).observe(document.body, {childList: true, subtree: true});
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();

